import { useState, useEffect, useCallback, useRef } from 'react';
import { contentApi } from '../services/api';
import type { 
  Content, 
  ContentFilters, 
  ContentFormData
} from '../types/content';

interface UseContentState {
  content: Content[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: ContentFilters;
}

interface UseContentReturn extends UseContentState {
  loadContent: () => Promise<void>;
  createContent: (data: ContentFormData) => Promise<Content>;
  updateContent: (id: string, data: Partial<ContentFormData>) => Promise<Content>;
  deleteContent: (id: string) => Promise<void>;
  archiveContent: (id: string) => Promise<Content>;
  restoreContent: (id: string) => Promise<Content>;
  retryTranscription: (id: string) => Promise<void>;
  setFilters: (filters: Partial<ContentFilters>) => void;
  setPage: (page: number) => void;
  refreshContent: () => Promise<void>;
}

export const useContent = (initialFilters: ContentFilters = {}): UseContentReturn => {
  // Prevent duplicate calls with loading ref
  const loadingRef = useRef(false);
  const [state, setState] = useState<UseContentState>({
    content: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    },
    filters: {
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      showArchived: false,
      ...initialFilters
    }
  });

  const loadContent = useCallback(async () => {
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Include search parameter in API call
      const apiParams = {
        ...state.filters,
        search: state.filters.search || undefined
      };
      
      const response = await contentApi.list(apiParams) as any;
      
      console.log('🔍 DEBUG: Raw API response:', response);
      
      // API response formatı: { success: true, data: [...], pagination: {...} }
      if (response.success) {
        setState(prev => ({
          ...prev,
          content: response.data || [],
          pagination: response.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0
          },
          loading: false
        }));
      } else {
        throw new Error(response.error || 'Failed to load content');
      }
    } catch (error) {
      console.error('🔍 DEBUG: Content loading error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load content',
        loading: false
      }));
    } finally {
      loadingRef.current = false;
    }
  }, [state.filters]);

  const createContent = useCallback(async (data: ContentFormData): Promise<Content> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await contentApi.create(data) as any;
      
      // API response formatı: { success: true, data: {...} }
      if (response.success) {
        const newContent = response.data;
        setState(prev => ({
          ...prev,
          content: [newContent, ...prev.content],
          loading: false
        }));
        return newContent;
      } else {
        throw new Error(response.error || 'Failed to create content');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to create content',
        loading: false
      }));
      throw error;
    }
  }, []);

  const updateContent = useCallback(async (id: string, data: Partial<ContentFormData>): Promise<Content> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await contentApi.update(id, data) as any;
      
      // API response formatı: { success: true, data: {...} }
      if (response.success) {
        const updatedContent = response.data;
        setState(prev => ({
          ...prev,
          content: prev.content.map(item => 
            item.id === id ? updatedContent : item
          ),
          loading: false
        }));
        return updatedContent;
      } else {
        throw new Error(response.error || 'Failed to update content');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update content',
        loading: false
      }));
      throw error;
    }
  }, []);

  const deleteContent = useCallback(async (id: string): Promise<void> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await contentApi.delete(id) as any;
      
      // API response formatı: { success: true }
      if (response.success) {
        // Optimistically remove from content list
        setState(prev => ({
          ...prev,
          content: prev.content.filter(item => item.id !== id),
          pagination: {
            ...prev.pagination,
            total: prev.pagination.total - 1
          },
          loading: false
        }));
      } else {
        throw new Error(response.error || 'Failed to delete content');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete content',
        loading: false
      }));
      throw error;
    }
  }, []);

  const archiveContent = useCallback(async (id: string): Promise<Content> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await contentApi.archive(id) as any;
      
      // API response formatı: { success: true, data: {...} }
      if (response.success) {
        const archivedContent = response.data;
        setState(prev => ({
          ...prev,
          content: prev.content.map(item => 
            item.id === id ? archivedContent : item
          ),
          loading: false
        }));
        return archivedContent;
      } else {
        throw new Error(response.error || 'Failed to archive content');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to archive content',
        loading: false
      }));
      throw error;
    }
  }, []);

  const restoreContent = useCallback(async (id: string): Promise<Content> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await contentApi.restore(id) as any;
      
      // API response formatı: { success: true, data: {...} }
      if (response.success) {
        const restoredContent = response.data;
        setState(prev => ({
          ...prev,
          content: prev.content.map(item => 
            item.id === id ? restoredContent : item
          ),
          loading: false
        }));
        return restoredContent;
      } else {
        throw new Error(response.error || 'Failed to restore content');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to restore content',
        loading: false
      }));
      throw error;
    }
  }, []);

  const retryTranscription = useCallback(async (id: string): Promise<void> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await contentApi.retryTranscription(id);
      
      // Refresh content to get updated status
      await loadContent();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to retry transcription',
        loading: false
      }));
      throw error;
    }
  }, [loadContent]);

  const setFilters = useCallback((newFilters: Partial<ContentFilters>) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...newFilters,
        page: newFilters.page || 1 // Reset to page 1 when filters change
      }
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        page
      }
    }));
  }, []);

  const refreshContent = useCallback(async () => {
    await loadContent();
  }, [loadContent]);

  // Fetch content when filters change
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  return {
    ...state,
    loadContent,
    createContent,
    updateContent,
    deleteContent,
    archiveContent,
    restoreContent,
    retryTranscription,
    setFilters,
    setPage,
    refreshContent
  };
};

// Hook for single content management
export const useContentItem = (id: string) => {
  const [content, setContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await contentApi.get(id) as any;
      
      // API response formatı: { success: true, data: {...} }
      if (response.success) {
        const contentData = response.data;
        if (contentData) {
          setContent(contentData);
        }
      } else {
        throw new Error(response.error || 'Failed to fetch content');
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch content');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const getUploadUrl = useCallback(async (fileName: string, fileType: string) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await contentApi.getUploadUrl(id, fileName, fileType) as any;
      
      // API response formatı: { success: true, data: {...} }
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to get upload URL');
      }
    } catch (error) {
      console.error('Failed to get upload URL:', error);
      setError(error instanceof Error ? error.message : 'Failed to get upload URL');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return {
    content,
    loading,
    error,
    fetchContent,
    getUploadUrl,
    refreshContent: fetchContent
  };
};

export default useContent;